// src/services/api/purchaseOrder.js
import { apiClient } from './base';

/**
 * Get user's accessible warehouses for purchase orders
 */
export const getUserWarehouses = async () => {
  try {
    const response = await apiClient.get('/procurement/purchase-orders/warehouses/accessible');
    return response.data;
  } catch (error) {
    throw error;
  }
};

/**
 * Create a new purchase order
 * @param {Object} orderData - Purchase order data
 */
export const createPurchaseOrder = async (orderData) => {
  try {
    const response = await apiClient.post('/procurement/purchase-orders', orderData);
    return response.data;
  } catch (error) {
    throw error;
  }
};

/**
 * Update existing purchase order
 * @param {number} orderId - Purchase order ID
 * @param {Object} orderData - Updated purchase order data
 */
export const updatePurchaseOrder = async (orderId, orderData) => {
  try {
    const response = await apiClient.put(`/procurement/purchase-orders/${orderId}`, orderData);
    return response.data;
  } catch (error) {
    throw error;
  }
};

/**
 * Get all purchase orders with filters
 * @param {Object} params - Query parameters
 */
export const getAllPurchaseOrders = async (params = {}) => {
  try {
    const response = await apiClient.get('/procurement/purchase-orders', { params });
    return response.data;
  } catch (error) {
    throw error;
  }
};

/**
 * Get purchase order by ID
 * @param {number} orderId - Purchase order ID
 */
export const getPurchaseOrderById = async (orderId) => {
  try {
    const response = await apiClient.get(`/procurement/purchase-orders/${orderId}`);
    return response.data;
  } catch (error) {
    throw error;
  }
};

/**
 * Mark purchase order as received
 * @param {number} orderId - Purchase order ID
 * @param {Object} receiveData - Receive data
 */
export const receivePurchaseOrder = async (orderId, receiveData) => {
  try {
    const response = await apiClient.post(`/procurement/purchase-orders/${orderId}/receive`, receiveData);
    return response.data;
  } catch (error) {
    throw error;
  }
};

/**
 * Cancel purchase order
 * @param {number} orderId - Purchase order ID
 * @param {Object} cancelData - Cancellation data
 */
export const cancelPurchaseOrder = async (orderId, cancelData) => {
  try {
    const response = await apiClient.post(`/procurement/purchase-orders/${orderId}/cancel`, cancelData);
    return response.data;
  } catch (error) {
    throw error;
  }
};

/**
 * Reverse a received purchase order and restore stock
 * @param {number} orderId - Purchase order ID
 * @param {Object} reverseData - Reversal data (cancellationReason optional)
 */
export const reversePurchaseOrder = async (orderId, reverseData) => {
  try {
    const response = await apiClient.post(`/procurement/purchase-orders/${orderId}/reverse`, reverseData);
    return response.data;
  } catch (error) {
    throw error;
  }
};

/**
 * Get purchase order statistics
 * @param {Object} params - Query parameters
 */
export const getPurchaseOrderStatistics = async (params = {}) => {
  try {
    const response = await apiClient.get('/procurement/purchase-orders/statistics/summary', { params });
    return response.data;
  } catch (error) {
    throw error;
  }
};
/**
 * Get the procurement audit trail of a purchase order
 * (PO changes, supplier payments, GRNs, RTVs, debit notes)
 */
export const getPurchaseOrderAuditHistory = async (orderId, params = {}) => {
  const response = await apiClient.get(`/procurement/purchase-orders/${orderId}/audit`, { params });
  return response.data;
};

/**
 * Get the procurement audit summary (total changes, last modified) of a purchase order
 */
export const getPurchaseOrderAuditSummary = async (orderId) => {
  const response = await apiClient.get(`/procurement/purchase-orders/${orderId}/audit/summary`);
  return response.data;
};
